import React from "react"
import { Box, Stack, Typography } from "@mui/material"
import { SystemMessageClasses, useStyles } from "./styles"
import { ICON_BY_SIZE, TYPOGRAPHY_VARIANTS_BY_SIZE } from "./helpers"
import { FaIcon } from "../../core/icons"

export type SystemMessageSize = "lg" | "sm"
export type SystemMessageVariant = "info" | "default"

export interface SystemMessageProps {
  title: React.ReactNode
  text: React.ReactNode
  icon: React.ReactNode
  size?: SystemMessageSize
  variant?: SystemMessageVariant
  outerClasses?: Partial<Record<SystemMessageClasses, string>>
  className?: string
  actions?: React.ReactNode
  "data-automation-id"?: string
}

const SystemMessage: React.FC<SystemMessageProps> = ({
  title,
  text,
  size = "lg",
  variant = "info",
  icon,
  outerClasses = {},
  children,
  className,
  actions,
  "data-automation-id": dataAutomationId
}) => {
  const { classes, cx } = useStyles({ size, variant })

  const isLargeSize = size === "lg"

  return (
    <Stack
      spacing={isLargeSize ? 5 : 4}
      direction="row"
      alignItems={!!children ? "flex-start" : "center"}
      className={cx(classes.container, outerClasses.container, className)}
      data-automation-id={dataAutomationId}
    >
      <Box className={cx(classes.iconContainer, outerClasses.iconContainer)}>
        {typeof icon === "string" ? (
          <Box className={cx(classes.defaultIcon, outerClasses.defaultIcon)}>
            <FaIcon solid size={ICON_BY_SIZE[size]} name={icon} />
          </Box>
        ) : (
          icon
        )}
      </Box>

      <Stack
        className={cx(classes.contentContainer, outerClasses.contentContainer)}
      >
        <Stack
          className={cx(classes.textContainer, outerClasses.textContainer)}
        >
          {typeof title !== "string" ? (
            title
          ) : (
            <Typography variant={TYPOGRAPHY_VARIANTS_BY_SIZE.title[size]}>
              {title}
            </Typography>
          )}

          {typeof text !== "string" ? (
            text
          ) : (
            <Typography
              variant={TYPOGRAPHY_VARIANTS_BY_SIZE.text[size]}
              className={outerClasses.text}
            >
              {text}
            </Typography>
          )}
        </Stack>

        {children}
      </Stack>
      {Boolean(actions) && (
        <Box
          className={cx(
            classes.actionsContainer,
            outerClasses.actionsContainer
          )}
        >
          {actions}
        </Box>
      )}
    </Stack>
  )
}

export default SystemMessage
